/**
 * Model Status Service
 *
 * @author Monika Gole <monika.gole@ncircletech.com>
 * @copyright (c) 2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { WdLocalEnvironmentService } from '../environment/wd-local-environment.service';
import { BehaviorSubject } from 'rxjs';
import { WdLocalSharedFunctionsService } from '../shared/wd-local-shared-functions.service';
import { IStatusMap } from 'src/app/interfaces/spools/spool-interfaces';
declare let THREE: any;

@Injectable({
  providedIn: 'root',
})
export class WdModelStatusService {
  /**
   * Spooling API URL
   */
  spoolingApiUrl: string;

  /**
   * Is loading
   */
  isLoading = new BehaviorSubject(false);

  /**
   * Constructor
   */
  constructor(
    public environment: WdLocalEnvironmentService,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
  ) {
    this.spoolingApiUrl = this.wdLocalSharedFunctions.spoolingApiUrl;
  }

  /**
   * Get model item statuses by model ID
   */
  async fetchModelItemStatusesById(modelId: string): Promise<any> {
    this.isLoading.next(true);

    const updatedModelId = this.extractModelId(modelId);

    try {
      return await this.wdOauth2HttpService.get(
        `${this.spoolingApiUrl}statuses/models/${updatedModelId}`,
      );
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        'Failed to model statuses data for model',
      );
    } finally {
      this.isLoading.next(false);
    }
  }

  /**
   * Extract model Id from model version Id
   */
  private extractModelId(modelId: string) {
    const underscoreIndex = modelId.indexOf('_');

    // Slice the string to get the part before the underscore
    const updatedModelId =
      underscoreIndex === -1 ? modelId : modelId.slice(0, underscoreIndex);

    return updatedModelId;
  }

  /**
   * Get mapping statuses data
   */
  async fetchMappingStatusesData(): Promise<any> {
    this.isLoading.next(true);

    try {
      return await this.wdOauth2HttpService.get(
        `${this.spoolingApiUrl}statuses/mappings`,
      );
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        'Failed to get statuses mappings data',
      );
    } finally {
      this.isLoading.next(false);
    }
  }

  /**
   * Update items statuses
   */
  async updateItemStatusInModel(modelId: string, data: any): Promise<void> {
    this.isLoading.next(true);

    this.applyUpdatedColorToItem(data);

    const elementIds = await this.mapDbIdToElementId(data.viewer, data.dbIds);

    const updatedData = this.prepareUpdatedItemData(
      elementIds,
      data.statusChangeResult,
    );

    const updatedModelId = this.extractModelId(modelId);

    try {
      await this.wdOauth2HttpService.put(
        `${this.spoolingApiUrl}statuses/models/${updatedModelId}?idType=modelId`,
        updatedData,
      );

      this.isLoading.next(false);
      this.wdErrorService.userSuccess('Success', 'Updated Items Status');
    } catch (error) {
      this.wdLocalSharedFunctions.parseErrors(
        error,
        'Failed to update item status',
      );
      throw error;
    }
  }

  /**
   * Apply updated colour to the items in forge viewer
   */
  private applyUpdatedColorToItem(data: any) {
    const statusColour = this.getItemStatusColor(
      data.statusChangeResult._id,
      data.statusesMappingsData,
    );
    data.dbIds.forEach((dbId) => {
      data.viewer.setThemingColor(
        dbId,
        this.hexToThreeVector4(statusColour),
        data.viewer.model,
      );
    });

    data.viewer.impl.toggleGhosting(false);
    data.viewer.clearSelection();
  }

  /**
   * Extract ElementId for given DbId
   */
  private mapDbIdToElementId(viewer: any, dbIds: number[]): Promise<string[]> {
    return new Promise((resolve, reject) => {
      viewer.model?.getBulkProperties(
        dbIds,
        ['ElementId'],
        (elements: any) => {
          const selectedIds = [];
          for (const element of elements) {
            const elementIdProp = element.properties.find(
              (prop: any) => prop.displayName === 'ElementId',
            );
            if (elementIdProp) {
              selectedIds.push(elementIdProp.displayValue);
            } else {
              reject;
            }
          }
          resolve(selectedIds);
        },

        reject,
      );
    });
  }

  /**
   * Prepare updated item status data for update function
   */
  prepareUpdatedItemData(elementIds: string[], updatedStatus: any) {
    const itemsData = [];

    elementIds.forEach((element) => {
      itemsData.push({
        itemId: element,
        status: updatedStatus.input.label,
      });
    });

    const updatedData = {
      items: itemsData,
      dateModified: Date.now(),
      modelGuid: null,
    };

    return updatedData;
  }

  /**
   * Convert Hex colour to RGBA Format
   */
  hexToRgba(hex: string): { r: number; g: number; b: number; a: number } {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b, a: 1 };
  }

  /**
   * Convert Hex colour to THREE.Vector4
   */
  hexToThreeVector4(hex: string): any {
    const { r, g, b, a } = this.hexToRgba(hex);
    return new THREE.Vector4(r / 255, g / 255, b / 255, a);
  }

  /**
   * Get item status colour using stored mappingIDd
   */
  getItemStatusColor(statusMapId: string, statusMappings: IStatusMap[]) {
    let colour;
    for (const item of statusMappings) {
      if (item._id === statusMapId) {
        colour = item.input.colour;
      }
    }
    return colour;
  }
}
